import { useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useOs } from '../../../api/queries/ordensDeServico'
import { Botao } from '../../../components/Botao'
import { AvisoDeErro, Carregando } from '../../../components/Estados'
import { IconeImprimir, IconeSeta } from '../../../components/Icones'
import { dataHora } from '../../../lib/datas'
import { OS } from '../textosDaOs'
import { DocumentoOs } from './DocumentoOs'
import './os.css'

/**
 * A moldura de impressão da ordem de serviço.
 *
 * Vive **fora do `Layout`** (ver `routes/router.tsx`), como a folha do TRP e a do RDM: menu,
 * cabeçalho e rodapé da aplicação não podem sair no papel. E fica **fora do portão de contratante**,
 * ao contrário do TRP — a ordem obriga a contratada, e o executor precisa poder imprimir o
 * documento que o obriga.
 *
 * A barra de controles é da tela, não do documento: `print:hidden`, e o jeito de saber se algo
 * pertence ao documento é perguntar se sai impresso.
 *
 * @returns a página do documento
 */
export function PaginaDocumentoOs() {
  const { id = '' } = useParams()
  const { data: ordem, isPending, isError, error } = useOs(Number(id))

  // Fixado na montagem: a data da impressão sai no rodapé, e um `new Date()` dentro do JSX faria
  // duas reconciliações do React discordarem no meio da impressão.
  const impressoEm = useMemo(() => dataHora(new Date().toISOString()), [])

  return (
    <div className="min-h-screen bg-slate-200 print:bg-white">
      <section
        aria-label="Ações do documento"
        className="sticky top-0 z-10 border-b border-slate-300 bg-white px-4 py-3 shadow-sm print:hidden"
      >
        <div className="mx-auto flex max-w-[210mm] flex-wrap items-center gap-4">
          <Link
            to={`/ordens-servico/${id}`}
            className="inline-flex items-center gap-1.5 text-sm text-niteroi-700 underline underline-offset-2"
          >
            <IconeSeta className="h-4 w-4 rotate-180" />
            Voltar para a ordem de serviço
          </Link>
          <Botao aparencia="primario" onClick={() => window.print()} disabled={!ordem}>
            <IconeImprimir className="h-4 w-4" />
            {OS.imprimir}
          </Botao>
          <p className="min-w-64 flex-1 text-sm text-slate-600">
            Na janela de impressão, escolha "Salvar como PDF" no lugar da impressora. Confira o documento
            na tela antes: ele sai exatamente assim.
          </p>
        </div>
      </section>

      <main className="px-4 py-6 print:p-0">
        {isPending && <Carregando texto="Montando o documento…" />}
        {isError && (
          <div className="mx-auto max-w-2xl">
            <AvisoDeErro erro={error} titulo="Não foi possível montar o documento" />
          </div>
        )}
        {ordem && <DocumentoOs ordem={ordem} emitidoEm={impressoEm} />}
      </main>
    </div>
  )
}
